import React, { FC, useCallback, useRef, useState } from 'react';
import {
	Box,
	Button,
	Card,
	IconButton,
	Slider,
	Typography,
} from '@mui/material';
import { SliderProps } from '@mui/material/Slider';
import { styled } from '@mui/material/styles';
import {
	Configuration,
	CONFIGURATION,
	PostConfiguration,
} from 'data/configuration';
import { Gutter } from 'components/Gutter';
import { authActions } from 'store/auth';
// import * as hl from '@nktkas/hyperliquid';
import { useDispatch } from 'react-redux';
import { useAccount } from 'wagmi';
import { useEthersSigner } from 'hooks/useEtherSigner';
import ConfirmationModal from 'pages/CreateAgent/ConfirmationAgent';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Form, Formik, useField } from 'formik';
import * as Yup from 'yup';

//const transport = new hl.HttpTransport();
//const newWallet: Wallet = Wallet.createRandom();

const Row = styled('div')`
	display: flex;
	flex-direction: row;
	align-items: center;
`;

const TwoColumnGrid = styled('div')(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'flex-start',
	gap: theme.spacing(2),
}));

const SliderContainer = styled(Box)(({ theme }) => ({
	margin: theme.spacing(2, 0),
}));

const SignalCard = styled(Card)(({ theme }) => ({
	padding: theme.spacing(5),
	backdropFilter: 'blur(14px)',
	background: '#ffffff55',
	marginTop: 20,
	boxShadow: 'none',
	border: 'none',
	borderRadius: 20,
}));

const StyledTitle = styled(Typography)``;

const StyledButton = styled(Button, {
	shouldForwardProp: (prop) => prop !== 'variant',
})<{ variant: 'contained' | 'outlined' }>(({ theme, variant }) => ({
	...(variant === 'contained' && {
		backgroundColor: '#2dbcbb',
		boxShadow: 'none',
		color: '#fff',
		padding: '10px 30px',
		borderRadius: 50,
		'&:hover': {
			backgroundColor: '#207e7d',
			boxShadow: 'none',
		},
		'&:disabled': {
			backgroundColor: '#2b3a4155',
			boxShadow: 'none',
			color: '#fff',
		},
	}),
	...(variant === 'outlined' && {
		backgroundColor: 'transparent',
		color: '#2dbcbb',
		border: '1px solid #36454F',
		boxShadow: 'none',
		'&:hover': {
			backgroundColor: 'rgba(54, 69, 79, 0.08)',
		},
		'&:disabled': {
			backgroundColor: '#2b3a4155',
			boxShadow: 'none',
			color: '#fff',
		},
	}),
}));

interface FormikTextFieldProps {
	name: string;
	type?: string;
	label: string;
	fullWidth?: boolean;
	margin?: 'none' | 'dense' | 'normal';
	InputProps?: any;
}

interface FormikLabeledSliderProps {
	label: string;
	name: string;
	sliderProps?: Partial<SliderProps>;
	disabled?: boolean;
}

const FormikLabeledSlider: FC<FormikLabeledSliderProps> = ({
	label,
	name,
	sliderProps = {},
	disabled,
}) => {
	const [field, meta, helpers] = useField(name);
	const displayValue = field.value;
	return (
		<SliderContainer>
			<Typography variant="subtitle1" gutterBottom>
				{`${label}: ${displayValue}%`}
			</Typography>
			<Slider
				disabled={disabled}
				value={field.value}
				onChange={(_, newValue) => {
					helpers.setValue(newValue);
				}}
				valueLabelDisplay="auto"
				sx={{
					color: '#36454F',
					'& .MuiSlider-thumb': {
						borderColor: '#36454F',
					},
					'&.Mui-disabled': {
						color: '#9a9a9a !important',
						'& .MuiSlider-track': {
							backgroundColor: '#9a9a9a !important',
						},
						'& .MuiSlider-rail': {
							color: '#9a9a9a !important',
						},
						'& .MuiSlider-thumb': {
							borderColor: '#9a9a9a !important',
						},
					},
					...(sliderProps.sx || {}),
				}}
				{...sliderProps}
			/>
			{meta.touched && meta.error ? (
				<Typography variant="caption" color="error">
					{meta.error}
				</Typography>
			) : null}
		</SliderContainer>
	);
};

const validationSchema = Yup.object().shape({
	max_amount_per_trade: Yup.number()
		.min(1, 'Minimum is 0')
		.max(100, 'Maximum is 100')
		.required('Required'),
	total_trade_amount: Yup.number()
		.min(0.1, 'Minimum is 0.5')
		.required('Required'),
	take_profit_per_trade: Yup.number()
		.min(1, 'Minimum is 0')
		.max(100, 'Maximum is 100')
		.required('Required'),
	stop_loss_per_trade: Yup.number()
		.min(1, 'Minimum is 0')
		.max(100, 'Maximum is 100')
		.required('Required'),
});

interface ConfigurationPanelProps {
	initialConfig?: Configuration;
	selectedAgent?: Omit<PostConfiguration, 'agentKey' | 'agentAddress'>;

	onSuccess?(): void;
}

const ConfigurationPanel: FC<ConfigurationPanelProps> = ({
	initialConfig,
	onSuccess,
}) => {
	const dispatch = useDispatch();
	const { isConnected, address } = useAccount();
	const signer: any = useEthersSigner();
	const [loading, setLoading] = useState(false);
	const [open, setOpen] = useState<boolean>(false);
	const [pendingValues, setPendingValues] = useState<any>(null);
	const formikSubmitActionsRef = useRef<any>(null);

	const handleSaveConfig = useCallback(
		async (values: any) => {
			/*try {
				setLoading(true);
				const metamaskClient = new hl.WalletClient({
					wallet: signer,
					transport,
				});

				const agentWallet: Wallet = newWallet;
				//@ts-expect-error
				const agentAddress: `0x${string}` =
					agentWallet.address.toLowerCase();

				await metamaskClient.approveAgent({
					agentAddress,
					agentName: AGENT,
				});

				await saveConfiguration({
					config: values,
					address,
					agentKey: agentWallet.privateKey,
					agent: AGENT,
					agentAddress: `${agentWallet.address.toLowerCase()}`,
				});
				localStorage.setItem('trading_config', JSON.stringify(values));
				onSuccess?.();
			} catch (e: any) {
				if (Array.isArray(e)) {
					e.forEach((error: any) => {
						toast(error.message || 'An error occurred', {
							type: 'error',
						});
					});
				} else {
					toast(e.message || 'An unexpected error occurred', {
						type: 'error',
					});
				}
			} finally {
				setLoading(false);
				formikSubmitActionsRef.current?.setSubmitting(false);
			}*/
		},
		[address, onSuccess, signer],
	);

	const initialValues = {
		max_amount_per_trade:
			initialConfig?.max_amount_per_trade ||
			CONFIGURATION.max_amount_per_trade,
		total_trade_amount:
			initialConfig?.total_trade_amount ||
			CONFIGURATION.total_trade_amount,
		take_profit_per_trade:
			initialConfig?.take_profit_per_trade ||
			CONFIGURATION.take_profit_per_trade,
		stop_loss_per_trade:
			initialConfig?.stop_loss_per_trade ||
			CONFIGURATION.stop_loss_per_trade,
	};

	return (
		<>
			<TwoColumnGrid>
				<Row>
					<IconButton
						onClick={() => history.back()}
						aria-label="back"
					>
						<ArrowBackIcon />
					</IconButton>
					<div
						style={{
							display: 'flex',
							width: '100%',
							alignItems: 'center',
							justifyContent: 'center',
							minWidth: 400,
							maxWidth: 700,
						}}
					>
						<StyledTitle
							variant="h5"
							gutterBottom
							fontWeight={600}
							margin={0}
						>
							Configure your agent
						</StyledTitle>
					</div>
					<StyledTitle variant="h5" gutterBottom fontWeight={600} />
				</Row>
			</TwoColumnGrid>
			<TwoColumnGrid>
				<>
					<Formik
						initialValues={initialValues}
						validationSchema={validationSchema}
						onSubmit={(values, actions) => {
							if (!isConnected) {
								dispatch(authActions.openDrawer(true));
								actions.setSubmitting(false);
								return;
							}
							setPendingValues(values);
							formikSubmitActionsRef.current = actions;
							setOpen(true);
						}}
					>
						{({ isSubmitting }) => (
							<Form>
								<Box display="flex" justifyContent="flex-start">
									<Box width="100%" maxWidth={700}>
										<SignalCard sx={{ minHeight: 'auto' }}>
											<Typography variant="h5">
												Create your personal agent, use
												its alpha.
											</Typography>
											<Typography variant="body1">
												No private keys stored, you can
												stop the agent or override it
												anytime.
											</Typography>
										</SignalCard>
										<SignalCard>
											<FormikLabeledSlider
												disabled={!!initialConfig}
												label="Total Trade Amount"
												name="total_trade_amount"
												sliderProps={{
													min: 1,
													max: 100,
													step: 0.5,
												}}
											/>
											<FormikLabeledSlider
												disabled={!!initialConfig}
												label="Max amount per trade"
												name="max_amount_per_trade"
												sliderProps={{
													min: 1,
													max: 100,
													step: 0.5,
												}}
											/>
											<FormikLabeledSlider
												disabled={!!initialConfig}
												label="Take profit per trade"
												name="take_profit_per_trade"
												sliderProps={{
													min: 1,
													max: 100,
													step: 0.5,
												}}
											/>
											<FormikLabeledSlider
												disabled={!!initialConfig}
												label="Stop loss per trade"
												name="stop_loss_per_trade"
												sliderProps={{
													min: 1,
													max: 100,
													step: 0.5,
												}}
											/>
										</SignalCard>
									</Box>
								</Box>
								<Gutter spacing={2} />
								<Box
									mt={3}
									display="flex"
									justifyContent="start"
								>
									{/*<StyledButton
										variant="contained"
										type="submit"
										size="large"
										disabled={
											isSubmitting ||
											!!initialConfig ||
											loading
										}
									>
										<Typography
											variant="h6"
											fontWeight="bold"
											py="6px"
											color="#fff"
											style={{ lineHeight: '120%' }}
										>
											{address
												? `Create Agent`
												: 'Connect Wallet'}
										</Typography>
									</StyledButton>*/}
								</Box>
							</Form>
						)}
					</Formik>
					<ConfirmationModal
						open={open}
						onConfirm={() => {
							setOpen(false);
							if (pendingValues) {
								handleSaveConfig(pendingValues);
							}
						}}
						onCancel={() => {
							setOpen(false);
							formikSubmitActionsRef.current?.setSubmitting(
								false,
							);
						}}
					/>
				</>
				<div />
			</TwoColumnGrid>
		</>
	);
};

export default ConfigurationPanel;
